import apiClient from './api';
import authService from './authService';

class SubscriptionService {
  // Check subscription status
  async checkSubscriptionStatus() {
    try {
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Subscription check timeout')), 10000); // 10 second timeout
      });

      const apiPromise = apiClient.get('/Subscription/status');
      
      const response = await Promise.race([apiPromise, timeoutPromise]);
      
      // Check if response is successful and has data
      if (response.data && response.data.success) {
        const statusData = response.data.data;
        
        // Return the status data
        return {
          status: statusData.status || 'INACTIVE',
          planName: statusData.planName || null,
          expiresOn: statusData.expiresOn || null,
        };
      } else {
        // If success is false or no data, return INACTIVE
        console.warn('Subscription status response not successful:', response.data);
        return { status: 'INACTIVE', planName: null, expiresOn: null };
      }
    } catch (error) {
      console.error('Subscription Status API Error:', error);
      console.error('Error response:', error.response?.data);
      
      // If API fails (404, 500, network error, timeout, etc.), assume no active subscription
      // This is the SAFE default - deny access rather than grant it
      return { status: 'INACTIVE', planName: null, expiresOn: null };
    }
  }

  // Get available subscription plans
  async getSubscriptionPlans() {
    try {
      const response = await apiClient.get('/Onboarding/subscriptions');
      return response.data.data; // Return the data array
    } catch (error) {
      console.error('Subscription Plans API Error:', error);
      throw error.response?.data?.message || 'Failed to fetch subscription plans.';
    }
  }

  // Get available states
  async getStates() {
    try {
      const response = await apiClient.get('/Onboarding/states');
      return response.data.data; // Return the data array
    } catch (error) {
      console.error('States API Error:', error);
      throw error.response?.data?.message || 'Failed to fetch states.';
    }
  }

  // Purchase subscription
  async purchaseSubscription(subscriptionPlanId, stateIds, billingCycle = 'monthly') {
    try {
      const user = authService.getCurrentUser();
      if (!user || !user.organizationId || !user.userId) {
        throw new Error('User information not found. Please login again.');
      }

      const payload = {
        subscriptionPlanId,
        billingCycle,
        organizationId: user.organizationId,
        userId: user.userId,
        stateIds,
        successUrl: `${window.location.origin}/payment-success`,
        cancelUrl: `${window.location.origin}/payment-cancel`,
      };

      const response = await apiClient.post('/Subscription/purchase', payload);
      return response.data.data; // Return { checkoutUrl }
    } catch (error) {
      console.error('Purchase API Error:', error);
      throw error.response?.data?.message || 'Failed to initiate subscription purchase.';
    }
  }
}

export default new SubscriptionService();